import moment from 'moment';
import React from 'react';
import { useHistory } from 'react-router-dom';
import * as Config from 'src/shared/config';

import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import { ReactComponent as CalendarIcon } from 'src/assests/icons/calendar-icon.svg';
import { ReactComponent as Clock } from 'src/assests/icons/clock-icon.svg';
import { ReactComponent as Human } from 'src/assests/icons/human-icon.svg';
import { ReactComponent as Location } from 'src/assests/icons/location-icon.svg';
import { ReactComponent as Soccer } from 'src/assests/icons/soccer-icon.svg';

import { useTokenContext } from 'src/account/shared/service/token.context';
import {
  calendarOptions,
  formatTime,
  getDurationFromTime,
  getInitials,
} from 'src/coach/service/coach.utils';
import { LocationVenueIcon } from 'src/coach/ui/class-details/components/svg';
import './classDates.sass';
import { eventDateAggregateInfo } from 'src/coach/ui/class-details/components/helpers';
import { MdNotes } from 'react-icons/md';

export const ClassDates = ({ classes = [], eventAggregateMap }) => {
  return (
    <div>
      {classes.map((cls) => (
        <ClassItem
          cls={cls}
          key={cls.id}
          eventAggregateMap={eventAggregateMap}
        />
      ))}
    </div>
  );
};

const ClassItem = ({ cls, eventAggregateMap }) => {
  const aggregateData = eventAggregateMap?.[cls?.id];
  const aggregateInfo = eventDateAggregateInfo(aggregateData);

  const history = useHistory();
  const { coachTokendecoded } = useTokenContext();

  const openEventDetails = (op) =>
    history.push(`/portal/class-day/${op.eventDate.id}`, op);

  const participantsCount = cls?.event?.isActiveEnrollmentsOverridden
    ? cls?.event?.overriddenActiveEnrollments
    : cls?.activeParticipants + cls?.singleDayParticipants || 0;
  const showAsInActive =
    cls?.event?.effectiveClassesWithZeroEnrollment ===
      'SHOW_AS_INACTIVE_ON_COACH_PORTAL' && participantsCount === 0;

  const isActiveClassBackground = cls.isActive
    ? ''
    : 'in-active-class-background';
  const isActiveClassFontColor = cls.isActive ? '' : 'in-active-class-color';
  const isActive = cls.isActive;

  return (
    <div
      key={cls.id}
      className={`${isActiveClassBackground} 
            card__layout-short card__margin-top`}
    >
      <div className="card-gap">
        <div
          style={{ display: 'contents' }}
          onClick={() => {
            openEventDetails({ eventDate: cls });
          }}
        >
          <div className="card--classes--layout">
            <CalendarIcon className="item" />

            <span className="card-classes-title--1 left-margin item">
              {moment(cls.startDateTimeInUTC).calendar(calendarOptions)}, &nbsp;
              <span className="card-classes-title--1-font-weight">
                {moment(cls.startDateTimeInUTC).format('ll')}
              </span>
            </span>
            <img
              height={36}
              className="item"
              src={Config.mediaUrl(cls.event.program?.logoUrl)}
              alt={cls.event.program?.name}
            />
          </div>
          <span className="card__horizontal-line" />
          <div className="card--classes--layout">
            <Clock />
            <span
              className={`${isActiveClassFontColor} card__classes--normal  left-margin`}
            >
              {formatTime(cls?.startTime || cls?.event?.startTime)} -{' '}
              {formatTime(cls?.endTime || cls?.event?.endTime)} |{' '}
              {getDurationFromTime(
                cls?.startTime || cls?.event?.startTime,
                cls?.endTime || cls?.event?.endTime,
              )}
            </span>
            {!isActive && <span className="in-active-date ">No class</span>}
            {showAsInActive && (
              <span className="in-active-date ">Inactive</span>
            )}
          </div>
          <div className="card--classes--layout">
            <Human />
            <span
              className={`card__classes--normal  left-margin ${isActiveClassFontColor} `}
            >
              Age: {cls.event.ageRangeLabel}
            </span>
            <span className="card__classes--tag left-margin">
              {participantsCount}{' '}
              {participantsCount === 0 || participantsCount > 1
                ? 'Participants'
                : 'Participant'}
            </span>
          </div>
          <div className="card--classes--layout">
            <Location />
            <span className="card__classes--link  left-margin">
              {cls.event.isLocationOverride
                ? cls.event.locationOverrideText
                : cls.event.location.name}
            </span>
          </div>
          {cls.event.venue && (
            <div className="card--classes--layout">
              <LocationVenueIcon />
              <span className="card__classes--normal  left-margin">
                {cls.event.venue.name}
              </span>
            </div>
          )}
          <div className="card--classes--layout">
            <Soccer />
            <span
              className={`card__classes--normal  left-margin ${isActiveClassFontColor} `}
            >
              {cls.event.isProgramOverride
                ? cls.event.programOverrideText
                : cls.event.program?.name}
            </span>
          </div>
          <div className="card--classes--layout">
            <span
              className={`fs-14 lh-20 ml-20 fc-gray-45 ${isActiveClassFontColor} `}
            >
              {cls.event?.effectiveName || cls.event?.label}
            </span>
          </div>
          {aggregateInfo?.length > 0 && (
            <div className="flex gap-2">
              <MdNotes size={16} />

              <div className="flex flex-col gap-2">
                <div>
                  <p
                    className={
                      'flex fs-14 tw-flex-col tw-gap-1 font-normal text-xs fc-gray-45'
                    }
                  >
                    {aggregateInfo.map((info) => {
                      return <span key={info.label}>{info.label}</span>;
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}
          {coachTokendecoded.franchiseIds?.length > 1 ? (
            <div className="card--classes--layout tw-justify-between">
              <span
                className={`card__classes--normal ${isActiveClassFontColor} `}
              >
                Provided by: {cls.event.franchise.name}
              </span>
              <span className="tw-text-xs tw-text-gray">ID: {cls.id}</span>
            </div>
          ) : (
            <div className="card--classes--layout tw-justify-between">
              <span />
              <span className="tw-text-xs tw-text-gray">ID: {cls.id}</span>
            </div>
          )}
        </div>
        <span className="card__horizontal-line" />
      </div>
      <div
        className={`${
          isActive ? '' : 'stipes'
        } card--classes--layout card-bottom-actions`}
      >
        <AvatarGroup spacing={15} max={4}>
          {cls?.staffAssignments?.length > 0 &&
            cls?.staffAssignments?.map((staff) => {
              return staff?.logoUrl ? (
                <Avatar
                  key={staff?.assigneeId || staff?.id}
                  alt={staff.firstName}
                  src={staff.logoUrl}
                />
              ) : (
                <Avatar
                  key={staff?.assigneeId || staff?.id}
                  alt={staff.firstName}
                >
                  {getInitials(`${staff.firstName} ${staff.lastName}`)}
                </Avatar>
              );
            })}
        </AvatarGroup>
      </div>
    </div>
  );
};
